import {ChangeDetectionStrategy, Component, Input} from '@angular/core';
import {IModalDismissResult, ModalControllerService} from '../../../shared-services/overlay/modal-controller.service';
import {SharedIconsModule} from '../../../../shared-modules/shared-icons/shared-icons.module';
import {IconColourEnum} from '../../../../shared-modules/shared-icons/models/icons-colour.enum';
import {
  ISharedActionSheetButton,
  ISharedActionSheetProps,
  ISharedActionSheetResult,
} from '../../../shared-models/modals/shared-action-sheet';

export const SHARED_ACTION_SHEET_CSS = 'shared-action-sheet-css';

@Component({
  selector: 'app-shared-action-sheet',
  templateUrl: './shared-action-sheet.component.html',
  styleUrls: ['./shared-action-sheet.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [SharedIconsModule],
})
export class SharedActionSheetComponent {

  @Input() header = '';
  @Input() buttons: ISharedActionSheetButton[] = [];

  protected readonly iconColourEnum = IconColourEnum;

  constructor(private readonly modalController: ModalControllerService) {
  }

  protected get actionButtons(): ISharedActionSheetButton[] {
    return this.buttons.filter((button: ISharedActionSheetButton) => button.role !== 'cancel');
  }

  protected get cancelButtons(): ISharedActionSheetButton[] {
    return this.buttons.filter((button: ISharedActionSheetButton) => button.role === 'cancel');
  }

  /**
   * Dismisses the sheet without selecting an action.
   */
  protected onCancel(): void {
    void this.modalController.dismiss({confirmed: false} as ISharedActionSheetResult);
  }

  /**
   * Dismisses the sheet with the chosen action id.
   */
  protected onSelectAction(button: ISharedActionSheetButton): void {
    if (button.role === 'cancel') {
      this.onCancel();
      return;
    }
    void this.modalController.dismiss({confirmed: true, selectedId: button.id} as ISharedActionSheetResult);
  }
}

/**
 * Opens the shared bottom action sheet and returns the dismiss result.
 */
export async function presentSharedActionSheet(
  modalController: ModalControllerService,
  props: ISharedActionSheetProps,
): Promise<IModalDismissResult<ISharedActionSheetResult>> {
  const modal = await modalController.create({
    component: SharedActionSheetComponent,
    componentProps: props,
    cssClass: [SHARED_ACTION_SHEET_CSS],
  });
  await modal.present();
  return modal.onDidDismiss() as Promise<IModalDismissResult<ISharedActionSheetResult>>;
}
